"use client";

import { api } from "@/api";
import type { CaptchaVerifyResult } from "@/common/aliyun-captcha";
import { REFERRAL_CODE_STORAGE_KEY } from "@/common/const";
import { useAliyunCaptcha } from "@/hooks/useAliyunCaptcha";
import { userStore } from "@/store/user";
import { safeLocalStorageGetItem } from "@/util/safeLocalStorage";
import { useRouter } from "@bprogress/next/app";
import { App } from "antd";
import { AxiosError, isAxiosError } from "axios";
import classNames from "classnames";
import { usePathname, useSearchParams } from "next/navigation";
import { useCallback, useEffect, useState } from "react";
import { EmailLoginForm } from "./components/EmailLoginForm";
import { VerificationCodeView } from "./components/VerificationCodeView";
import styles from "./index.module.css";

const OAUTH_FUNCTION_PARAM = "vid_muse";
const MESSAGE_DURATION = 5; // Toast 显示时长（秒）

export default function Login() {
  const { message } = App.useApp();
  const router = useRouter();
  const searchParams = useSearchParams();
  const pathname = usePathname();
  const [email, setEmail] = useState("");
  const [isCodeSent, setIsCodeSent] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string>("");
  const [referralCode, setReferralCode] = useState<string>("");

  // 检查 OAuth 错误参数
  useEffect(() => {
    const errorParam = searchParams.get("error");
    const errorMessage = searchParams.get("message");

    if (errorParam && errorMessage) {
      message.error(decodeURIComponent(errorMessage), MESSAGE_DURATION);

      // 清除 URL 参数
      const params = new URLSearchParams(searchParams.toString());
      params.delete("error");
      params.delete("message");
      const newQuery = params.toString();
      router.replace(`${pathname}${newQuery ? `?${newQuery}` : ""}`);
    }
  }, [searchParams, message, pathname, router]);

  useEffect(() => {
    const storedReferralCode = safeLocalStorageGetItem(
      REFERRAL_CODE_STORAGE_KEY,
    );
    setReferralCode(storedReferralCode?.trim() || "");
  }, []);

  // 验证码验证回调
  const handleCaptchaVerify = useCallback(
    async (captchaVerifyParam: string): Promise<CaptchaVerifyResult> => {
      try {
        // 向后端发送验证码验证参数和邮箱,获取验证结果
        await api.user.sendLoginCode({
          email: email.trim(),
          captchaVerifyParam,
        });

        setIsCodeSent(true);
        setError("");

        return {
          captchaResult: true,
          bizResult: true,
        };
      } catch (error: unknown) {
        if (isAxiosError(error)) {
          const errorMessage = error.response?.data?.message;
          message.error(errorMessage, MESSAGE_DURATION);
          setError(errorMessage);
        }

        return {
          captchaResult: false,
          bizResult: false,
        };
      }
    },
    [email, message],
  );

  const handleAuthRedirect = (provider: "google" | "discord") => {
    const referralParam = referralCode
      ? `&referral_code=${encodeURIComponent(referralCode)}`
      : "";
    window.location.href = `/auth/oauth2/${provider}?function=${OAUTH_FUNCTION_PARAM}${referralParam}`;
  };

  const handleGoogleLogin = useCallback(() => {
    handleAuthRedirect("google");
  }, []);

  const handleDiscordLogin = useCallback(() => {
    handleAuthRedirect("discord");
  }, []);

  const { handleVerify: handleEmailSubmit, elementId } = useAliyunCaptcha({
    onVerify: handleCaptchaVerify,
  });

  const handleCodeSubmit = useCallback(
    async (verificationCode: string) => {
      if (!verificationCode || verificationCode.length !== 6) return;

      setIsLoading(true);
      setError("");

      try {
        const { data } = await api.user.loginWithCode({
          email: email.trim(),
          code: verificationCode,
          referralCode: referralCode || undefined,
        });
        // Fetch user data immediately after successful login
        await userStore.fetchUser();
        // Redirect to home
        router.push(
          `/?loginSuccess=true${data.newUser ? "&newUser=true" : ""}`,
        );
      } catch (error) {
        const errorMessage = "Incorrect verification code.";
        message.error(
          (error as AxiosError).message || errorMessage,
          MESSAGE_DURATION,
        );
        setError(errorMessage);
      } finally {
        setIsLoading(false);
      }
    },
    [email, message, router],
  );

  const handleCloseBtnClick = useCallback(() => {
    if (isCodeSent) {
      // 如果在验证码状态，返回到邮箱输入
      setIsCodeSent(false);
    } else {
      // 否则关闭登录页面
      router.push("/");
    }
  }, [isCodeSent, router]);

  return (
    <div
      className={classNames(
        styles.container,
        "relative bg-background-body flex items-start pt-0 sm:pt-[122px] justify-center min-h-screen",
      )}
    >
      <div id={elementId}></div>
      {/* 主登录框 */}
      {!isCodeSent ? (
        <EmailLoginForm
          email={email}
          isLoading={isLoading}
          onEmailChange={setEmail}
          onEmailSubmit={handleEmailSubmit}
          onGoogleLogin={handleGoogleLogin}
          onDiscordLogin={handleDiscordLogin}
          onClose={handleCloseBtnClick}
        />
      ) : (
        <VerificationCodeView
          email={email}
          isLoading={isLoading}
          error={error}
          onCodeSubmit={handleCodeSubmit}
          onResendCode={handleEmailSubmit}
          onClose={handleCloseBtnClick}
        />
      )}
    </div>
  );
}
